import { Component, OnInit, ViewChild, OnDestroy, AfterContentInit, HostListener } from '@angular/core';
import { AppState, ShowRequest, RoleMgr } from '../core';
import { TabsetComponent, TabDirective } from 'ngx-bootstrap/tabs';
import { ConfirmModalComponent } from '../shared/components/confirm-modal/confirm-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { FesBaseComponent } from '../shared/components/fes-base.component';
import { Observable, Subject, timer } from 'rxjs';
import { AppTab } from '../core/app-tab';
import { HasUnsavedData } from '../core/security';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Location } from '@angular/common';
import { DriveTestService, PathTestService, ScoutTaskService, TaskService } from '../core/services';


@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent extends FesBaseComponent implements OnInit, AfterContentInit, OnDestroy, HasUnsavedData {

  @ViewChild(TabsetComponent) tabset
  errorMsg: string = "";
  public tabs : AppTab[] = []

  constructor(
    appState: AppState,
    private modalService: BsModalService,
    public roleMgr: RoleMgr,
    public router: Router,
    public route: ActivatedRoute,
    private location: Location,
    private scoutTaskService: ScoutTaskService,
    private driveTestService: DriveTestService,
    private pathTestService: PathTestService,
    private taskService: TaskService ) {
    super(appState)
  }

  ngOnInit(): void {
    this.subscribe(this.appState.showView, (vi : ShowRequest) => {
      if ( vi?.viewMode == 'tab' ) {
        this.showDetails(vi)
      }
    })
    this.appState.confirmExit = (exitFn, noExitFn = () => {}) => {
      let tabTitles = this.tabs.filter(t=>t.hasUnsavedData()).map(t=>t.viewInfo.title)
      if ( !tabTitles.length ) {
        exitFn()
      } else {
        let initialState = {
          onYes: exitFn,
          onNo: noExitFn,
          title: "Unsaved Work",
          question: `<div class="alert alert-danger">Unsaved work on ${tabTitles.length} tab(s).</div>
          <div class="d-inline-block mx-auto"><ul>${tabTitles.map(n=>'<li>'+n+'</li>')}</ul><div>
          <div class="pb-2">Discard all changes?</div>`,
          focusOnNo: true,
          yesStyle: 'btn btn-danger'
        }
        this.modalService.show(ConfirmModalComponent, {initialState} );
      }
    }
    this.initTabs()
  }

  ngAfterContentInit() {
    this.appState.doWhenReady( ()=>{
      // TODO - support url params to pass in array of tabs to open instead of just one)
      this.subscribe( this.route.queryParams, async (params: Params) => {
        const itemType = params.itemType || 'home'
        const itemId = params.itemId
        const taskId = params.taskId
        if ( taskId && itemType ) {
          let task : any;
          switch ( itemType ) {
            case 'scout path':
            case 'scout task':
            case 'scout report':
            case 'scout data report':
              task = await this.scoutTaskService.getById(taskId)
              break;
            case 'path test':
              task = await this.pathTestService.getById(taskId)
              break;
            case 'drive test':
              task = await this.driveTestService.getById(taskId)
              break;
            default:
              task = await this.taskService.getById(taskId)
          }
          if ( task ) {
            this.showDetails( new ShowRequest('tab',itemType,itemId,task))
            return
          } else {
            this.appState.publishError('No task found for ID: ' + taskId)
          }
        } else if ( itemType != 'home' ) {
          const typesWithoutTask = [ 'point', 'building', 'path', 'task list', 'post processing', 'user list',
              'building status', 'path status', 'file import', 'location logs', 'path test group', 'drive test group',
              't-mobile location logs', 'correlation', 'correlation dashboard', 'correlation summary', 'handsets', 'missing handsets',
              'call logs', 'dialer call logs', 'dialer config', 'rxdb buildings', 'rxdb points', 'rxdb paths', 'rxdb waypoints',
              'points audit','volunteer list', 'volunteer tasks','volunteer task points','volunteer task',
              'markets', 'rut call logs', 'volunteer handsets', 'volunteer points', 'path test', 'drive test', 'event log']
          if ( typesWithoutTask.find(t=>t==itemType) ) {
            this.showDetails( new ShowRequest('tab',itemType,itemId,null))
            return
          } else {
            this.appState.publishError('Task ID required for tab "' + itemType + '"')
          }
        }
        this.showDetails( new ShowRequest('tab', 'home', 'home-id', null))
      })
    })
  }

  ngOnDestroy(): void {
    this.destroy()
    this.appState.confirmExit = exitFn => exitFn() // call exit directly when no home component
  }


  initTabs() {
    let homeTab = new AppTab()
    homeTab.viewInfo = new ShowRequest('tab','home','home-id')
    this.tabs.push(homeTab)

    this.tabs.forEach( tab => {
      tab.url = this.router.createUrlTree([], {relativeTo: this.route, queryParams:{itemType: tab.viewInfo.viewType}}).toString()
    })
  }


  hasUnsavedData = () : Observable<boolean> | boolean => {
    console.log('hasUnsavedData()')
    let unsavedDataResult : Observable<boolean> | boolean = this.tabs.some(t=>t.hasUnsavedData())
    if ( unsavedDataResult !== false ) {
      let unsavedDataSubject = new Subject<boolean>()
      this.appState.confirmExit(
        () => unsavedDataSubject.next(false),
        () => unsavedDataSubject.next(true)
      )
      return unsavedDataSubject
    }
    return unsavedDataResult
  }


  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if ( !this.appState.isElectron() ) {
      console.log('unloadNotification('+$event+')')
      if ( this.tabs.some(t=>t.hasUnsavedData()) ) {
        $event.preventDefault();
        $event.returnValue = '';
      } else {
        const msg = 'closed-instance-notice'
        console.log('posting testbed-client-app message: ' + msg)
        const appChannel = new BroadcastChannel('testbed-client-app');
        appChannel.postMessage(msg);
      }
    }
  }


  removeTab(index:number) {
    const tab = this.tabs[index]
    tab.remove(this.tabset.tabs[index])
  }


  findTab = (vi:ShowRequest) : AppTab => {
    const tabs = this.tabs.filter( t => t.viewInfo.viewType == vi.viewType )
    //let tab = tabs.find( t => t.id == vi.id )
    const tab = tabs.find( t => {
      if ( t.viewInfo.matches ) {
        return t.viewInfo.matches(vi)
      } else {
        return t.viewInfo.id == vi.id
      }
    })
    return tab
  }


  showDetails = (viewInfo:ShowRequest) => {
    console.log('showDetails()')
    console.log("  taskId: " + viewInfo.task?._id)
    console.log("  itemType: " +  viewInfo.viewType)
    console.log("  itemId: " + viewInfo.id)
    let tab : AppTab = this.findTab(viewInfo)
    this.tabs.forEach(tab=>this.onTabChange(tab,false))
    if ( !tab ) {
      tab = new AppTab()
      tab.closable = true
      tab.viewInfo = viewInfo
      tab.close = () => { const tabIndex = this.tabs.findIndex(t=>t==tab); this.removeTab(tabIndex) }
      tab.remove = ( te : TabDirective ) => this.removeBootstrapTab(te,tab)

      let params : any = {}
      if ( viewInfo.viewType ) {
        params.itemType = viewInfo.viewType
      }
      if ( viewInfo.id ) {
        params.itemId = viewInfo.id
      }
      if ( viewInfo.task ) {
        params.taskId = viewInfo.task._id
      }
      tab.url = this.router.createUrlTree([], {relativeTo: this.route, queryParams:params}).toString()
      this.tabs.push(tab)
    }
    timer(0).subscribe(ms=>this.onTabChange(tab,true))
  }


  removeBootstrapTab( te : TabDirective, tab : AppTab ) {
    //console.log(`removeBootstrapTab(${tab.viewInfo.title}) active: ${tab.active}` )
    if ( tab.active ) {
      this.tabHistory = this.tabHistory.filter( t=>t!=tab)
      //this.tabHistory.forEach(t=>console.log(`History tab: ${t.viewInfo.title}, active: ${t.active}`))
      let lastActive = this.tabHistory.pop()
      while ( lastActive == tab ) {
        lastActive = this.tabHistory.pop()
      }
      if ( lastActive ) {
        this.tabs.forEach(t=>t.active=t==lastActive)
      }
    }
    this.tabs = this.tabs.filter( t => t != tab )
    if ( this.tabs.every(t=>!t.active)) {
      this.tabs[0].active = true
      this.tabs[0].setSelected(true)
    }
    this.tabset.removeTab(te,{reset:false})
  }

  tabHistory : AppTab[] = []
  get lastTab() { return this.tabHistory[this.tabHistory.length-1]}


  onTabChange( tab : AppTab, bSelected : boolean ) {
    //console.log(`onTabChange(tab=${tab.viewInfo?.title.value},bSelected=${bSelected},active=${tab.active})`)
    const bSameTab = tab == this.lastTab
    if ( bSelected ) {
      if ( !bSameTab ) {
        if ( tab.url ) {
          this.location.go(tab.url);
        }
        this.tabHistory.push(tab)
        if ( this.tabHistory.length > 10 ) {
          this.tabHistory.shift()
        }
      }
    }
    if ( tab.active != bSelected || (bSelected&&!bSameTab) ) {
      tab.active = bSelected
      if ( tab.setSelected ) {
        //console.log('\ttab.setSelected('+bSelected+') title=' + tab.viewInfo?.title.value)
        tab.setSelected(bSelected)
      }
    }
  }


  //When a db table filter or context is changed, update bookmarkable url in browser
  updateUrlItemId = (itemId: string,tab:AppTab) => {
    //console.log(`updateUrlItemId(itemId:${itemId})`)
    const oldUrl = this.router.parseUrl(tab.url)
    let params = oldUrl.queryParams
    params.itemId=itemId
    tab.url = this.router.createUrlTree([], {relativeTo: this.route, queryParams:params}).toString()
    tab.viewInfo.id = itemId
    //console.log('tab url: ' + tab.url)
    this.location.go(tab.url);
  }

}
